// pages/DealsPage.jsx
// Combined Active Orders + Tracking + Trending Deals

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { 
  ChevronLeft, 
  Clock, 
  Users, 
  Share2, 
  TrendingUp, 
  Package,
  MapPin,
  Truck,
  CheckCircle,
  AlertCircle,
  Flame,
  Coins
} from 'lucide-react';
import { GET_USER_PURCHASES, GET_ALL_PRODUCTS } from '../apollo/queries';
import { useAuth } from '../context/AuthContext';
import { useTokens } from '../context/TokenContext';
import { formatTokens } from '../utils/tokenEconomy';
import BottomNav from '../components/common/BottomNav';

const DealsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { balance } = useTokens();
  const [activeTab, setActiveTab] = useState('active');

  // Fetch user's purchases
  const { data: purchasesData, loading: purchasesLoading } = useQuery(GET_USER_PURCHASES, {
    variables: {},
    fetchPolicy: 'cache-and-network'
  });

  // Fetch all products for trending deals
  const { data: productsData, loading: productsLoading } = useQuery(GET_ALL_PRODUCTS, {
    variables: { limit: 20 },
    fetchPolicy: 'cache-and-network'
  });

  // Filter active orders (PENDING, CONFIRMED, PROCESSING)
  const activeOrders = useMemo(() => {
    if (!purchasesData?.myPurchases) return [];
    return purchasesData.myPurchases.filter(p => 
      ['PENDING', 'CONFIRMED', 'PROCESSING'].includes(p.status)
    );
  }, [purchasesData]);

  // Get recent completed orders for tracking
  const recentOrders = useMemo(() => {
    if (!purchasesData?.myPurchases) return [];
    return purchasesData.myPurchases
      .filter(p => ['SHIPPED', 'DELIVERED'].includes(p.status))
      .slice(0, 5);
  }, [purchasesData]);

  // Mock trending deals (products with momentum)
  const trendingDeals = useMemo(() => {
    if (!productsData?.products) return [];
    return productsData.products
      .filter(p => p.basePrice > 0)
      .slice(0, 6)
      .map(product => ({
        ...product,
        buyersCount: Math.floor(Math.random() * 15) + 3,
        targetForNextTier: Math.floor(Math.random() * 5) + 2,
        nextTierDiscount: Math.floor(Math.random() * 10) + 10,
        hoursLeft: Math.floor(Math.random() * 48) + 12
      }));
  }, [productsData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'text-yellow-600 bg-yellow-50';
      case 'CONFIRMED': return 'text-blue-600 bg-blue-50';
      case 'PROCESSING': return 'text-purple-600 bg-purple-50';
      case 'SHIPPED': return 'text-indigo-600 bg-indigo-50';
      case 'DELIVERED': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING': return <Clock className="w-4 h-4" />;
      case 'CONFIRMED': return <CheckCircle className="w-4 h-4" />;
      case 'PROCESSING': return <Package className="w-4 h-4" />;
      case 'SHIPPED': return <Truck className="w-4 h-4" />;
      case 'DELIVERED': return <CheckCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const handleShare = (product) => {
    const shareUrl = `${window.location.origin}/product/${product.id || product._id}`;
    if (navigator.share) {
      navigator.share({
        title: product.name,
        text: `Join my group buy and we both save! ${product.name}`,
        url: shareUrl
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied! Share with friends to unlock more savings.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const timestamp = Number(dateString);
    const date = isNaN(timestamp) ? new Date(dateString) : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold">Deals</h1>
            <button 
              onClick={() => navigate('/wallet')} 
              className="flex items-center gap-1 bg-yellow-50 px-3 py-1.5 rounded-full"
            >
              <Coins className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-700">
                {formatTokens(balance || 0)}
              </span>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex mt-4 border-b">
            <button
              onClick={() => setActiveTab('active')}
              className={`flex-1 pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'active' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500'
              }`}
            >
              My Orders ({activeOrders.length})
            </button>
            <button
              onClick={() => setActiveTab('trending')}
              className={`flex-1 pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'trending' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500'
              }`}
            >
              Trending Deals
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {activeTab === 'active' ? (
          <div className="space-y-4">
            {purchasesLoading ? (
              <div className="text-center py-8 text-gray-500">Loading orders...</div>
            ) : activeOrders.length === 0 && recentOrders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">No active orders</h3>
                <p className="text-gray-500 mb-4">Start shopping to see your orders here</p>
                <button
                  onClick={() => navigate('/home')}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium"
                >
                  Browse Products
                </button>
              </div>
            ) : (
              <>
                {/* Active Orders */}
                {activeOrders.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-3">Active Orders</h3>
                    {activeOrders.map((order) => (
                      <div 
                        key={order.id} 
                        className="bg-white rounded-lg shadow-sm p-4 mb-3"
                        onClick={() => navigate(`/orders/${order.id}`)}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-medium">
                              {order.products?.length || 0} item{order.products?.length !== 1 ? 's' : ''}
                            </p>
                            <p className="text-sm text-gray-500">{formatDate(order.createdAt)}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(order.status)}`}>
                            {getStatusIcon(order.status)}
                            {order.status}
                          </span>
                        </div>

                        {/* Progress to next tier */}
                        {order.status === 'PENDING' && (
                          <div className="bg-blue-50 rounded-lg p-3 mb-3">
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-blue-700">4 more buyers for 15% off!</span>
                              <span className="text-blue-600 font-medium">2d 14h left</span>
                            </div>
                            <div className="h-2 bg-blue-200 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-600 rounded-full" style={{ width: '60%' }} />
                            </div>
                          </div>
                        )}

                        {/* Share CTA for pending orders */}
                        {order.status === 'PENDING' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShare({ id: order.products?.[0]?.product?.id, name: order.products?.[0]?.product?.name });
                            }}
                            className="w-full bg-gradient-to-r from-purple-500 to-blue-500 text-white py-2 rounded-lg font-medium flex items-center justify-center gap-2"
                          >
                            <Share2 className="w-4 h-4" />
                            Share to Drop Your Price
                          </button>
                        )}

                        {/* Tracking for shipped orders */}
                        {order.status === 'SHIPPED' && (
                          <div className="flex items-center gap-2 text-sm text-indigo-600">
                            <Truck className="w-4 h-4" />
                            <span>Out for delivery</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Recent Completed */}
                {recentOrders.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-3">Recent Orders</h3>
                    {recentOrders.map((order) => (
                      <div 
                        key={order.id} 
                        className="bg-white rounded-lg shadow-sm p-4 mb-3 opacity-75"
                        onClick={() => navigate(`/orders/${order.id}`)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">
                              {order.products?.length || 0} item{order.products?.length !== 1 ? 's' : ''}
                            </p>
                            <p className="text-sm text-gray-500">{formatDate(order.createdAt)}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(order.status)}`}>
                            {getStatusIcon(order.status)}
                            {order.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* View All Orders Link */}
                <button
                  onClick={() => navigate('/orders')}
                  className="w-full text-center text-blue-600 font-medium py-3"
                >
                  View All Orders →
                </button>
              </>
            )}
          </div>
        ) : (
          /* Trending Deals Tab */
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              🔥 These deals are close to unlocking the next price tier!
            </p>
            
            {productsLoading ? (
              <div className="text-center py-8 text-gray-500">Loading deals...</div>
            ) : trendingDeals.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No trending deals right now</div>
            ) : (
              trendingDeals.map((deal) => (
                <div 
                  key={deal.id} 
                  className="bg-white rounded-lg shadow-sm overflow-hidden"
                  onClick={() => navigate(`/product/${deal.id}`)}
                >
                  <div className="p-4">
                    <div className="flex gap-3">
                      {/* Product Image */}
                      <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        {deal.images?.[0] ? (
                          <img 
                            src={deal.images?.[0]?.full || deal.images?.[0]?.thumbnail || deal.images?.[0]} 
                            alt={deal.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 line-clamp-1">{deal.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-lg font-bold text-green-600">${deal.currentPrice?.toFixed(2)}</span>
                          {deal.basePrice > deal.currentPrice && (
                            <span className="text-sm text-gray-400 line-through">${deal.basePrice?.toFixed(2)}</span>
                          )}
                        </div>
                        
                        {/* Momentum indicator */}
                        <div className="flex items-center gap-1 mt-1 text-sm">
                          <Flame className="w-4 h-4 text-orange-500" />
                          <span className="text-orange-600 font-medium">
                            {deal.buyersCount} buying
                          </span>
                          <span className="text-gray-500">
                            • {deal.targetForNextTier} more for {deal.nextTierDiscount}% off!
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-3">
                      <div className="h-2 bg-orange-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-orange-400 to-red-500 rounded-full transition-all"
                          style={{ width: `${(deal.buyersCount / (deal.buyersCount + deal.targetForNextTier)) * 100}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>{deal.hoursLeft}h left</span>
                        <span>Next tier: ${(deal.currentPrice * (1 - deal.nextTierDiscount/100)).toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/product/${deal.id}`);
                        }}
                        className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-medium text-sm"
                      >
                        Join Deal
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShare(deal);
                        }}
                        className="px-4 py-2 border border-purple-300 text-purple-600 rounded-lg"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default DealsPage;